import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Path, UploadSimple, Crosshair, Trash, DownloadSimple } from "@phosphor-icons/react";
import { Telemetry } from "../lib/api";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

// Flight-log import (DJI .SRT / Airdata-Litchi CSV) → GPS, bearing, segment on photos.
export function TelemetryPanel({ session, onApplied }) {
  const fileRef = useRef(null);
  const [tele, setTele] = useState(null);
  const [offset, setOffset] = useState(0);
  const [tolerance, setTolerance] = useState(5);
  const [overwrite, setOverwrite] = useState(false);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const load = () => Telemetry.get(session.id).then(setTele).catch(() => setTele(null));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [session.id]);

  const importLog = async (file) => {
    setBusy(true);
    try {
      const text = await file.text();
      const r = await Telemetry.import(session.id, file.name, text);
      setTele(r); setOpen(true);
      toast.success(`FLIGHT LOG: ${r.points} POINTS · ${r.segments.length} SEGMENT${r.segments.length === 1 ? "" : "S"}`);
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "IMPORT FAILED").toUpperCase().slice(0, 120));
    } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true);
    try {
      const r = await Telemetry.apply(session.id, { offset_seconds: Number(offset) || 0, tolerance_seconds: Number(tolerance) || 5, overwrite });
      toast.success(`MATCHED ${r.matched} · KEPT EXIF ${r.kept_exif_gps} · UNMATCHED ${r.unmatched}`);
      await load(); onApplied?.();
    } catch (e) {
      toast.error(String(e?.response?.data?.detail || "APPLY FAILED").toUpperCase().slice(0, 120));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm("Remove the flight log? Photos keep any GPS already written.")) return;
    await Telemetry.remove(session.id); setTele(null); toast.success("FLIGHT LOG REMOVED");
  };

  const downloadGeo = async () => {
    const g = await Telemetry.geojson(session.id);
    const blob = new Blob([JSON.stringify(g, null, 2)], { type: "application/geo+json" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob);
    a.download = `${session.name.replace(/[^a-zA-Z0-9-_]+/g, "-")}.geojson`; a.click(); URL.revokeObjectURL(a.href);
  };

  const field = "border-2 border-black px-2 py-1 font-mono text-xs w-20 bg-white";
  return (
    <div className="border-2 border-black mb-6" data-testid="telemetry-panel">
      <button onClick={() => setOpen((o) => !o)} className="w-full flex items-center justify-between px-3 py-2 text-left" data-testid="telemetry-toggle">
        <span className="flex items-center gap-2 font-display font-bold tracking-tighter"><Path size={18} weight="bold" /> FLIGHT LOG / TELEMETRY</span>
        <span className="label-mono text-[10px] text-neutral-500" data-testid="telemetry-summary">
          {tele ? `${tele.points} PTS · ${tele.segments.length} SEG · ${tele.source.toUpperCase()}` : "NONE IMPORTED"}
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 space-y-3 border-t-2 border-black pt-3">
          <p className="text-xs font-mono text-neutral-600">
            Import a DJI <b>.SRT</b> or an Airdata / Litchi <b>.CSV</b>. Photos without GPS get the nearest point by capture time;
            every photo gets a flight-segment index. GPS is written into each JPEG's EXIF.
          </p>
          <input ref={fileRef} type="file" accept=".srt,.csv,text/plain,text/csv" className="hidden" data-testid="telemetry-file-input"
                 onChange={(e) => { if (e.target.files?.[0]) importLog(e.target.files[0]); e.target.value = ""; }} />
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => fileRef.current?.click()} disabled={busy} className="btn-flat-outline text-xs" data-testid="telemetry-import-btn">
              <UploadSimple size={14} weight="bold" /> {tele ? "REPLACE LOG" : "IMPORT LOG"}
            </button>
            {tele && (
              <>
                <label className="label-mono text-[10px] flex items-center gap-1">OFFSET s <input className={field} type="number" value={offset} onChange={(e) => setOffset(e.target.value)} data-testid="telemetry-offset" /></label>
                <label className="label-mono text-[10px] flex items-center gap-1">TOLERANCE s <input className={field} type="number" min="0.5" value={tolerance} onChange={(e) => setTolerance(e.target.value)} data-testid="telemetry-tolerance" /></label>
                <label className="label-mono text-[10px] flex items-center gap-1"><input type="checkbox" checked={overwrite} onChange={(e) => setOverwrite(e.target.checked)} data-testid="telemetry-overwrite" /> OVERWRITE EXIF GPS</label>
                <button onClick={apply} disabled={busy} className="btn-flat text-xs" data-testid="telemetry-apply-btn"><Crosshair size={14} weight="bold" /> APPLY TO PHOTOS</button>
                <button onClick={downloadGeo} className="btn-flat-outline text-xs" data-testid="telemetry-geojson-btn"><DownloadSimple size={14} weight="bold" /> GEOJSON</button>
                <button onClick={remove} className="btn-flat-outline text-xs" data-testid="telemetry-remove-btn"><Trash size={14} weight="bold" /></button>
              </>
            )}
          </div>
          {tele && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs font-mono" data-testid="telemetry-details">
              <div className="border border-neutral-300 p-2"><div className="label-mono text-[10px] text-neutral-500">FILE</div>{tele.filename}</div>
              <div className="border border-neutral-300 p-2"><div className="label-mono text-[10px] text-neutral-500">SPAN</div>{fmt(tele.start)}<br />{fmt(tele.end)}</div>
              <div className="border border-neutral-300 p-2"><div className="label-mono text-[10px] text-neutral-500">LAST APPLY</div>
                {tele.last_apply ? `${tele.last_apply.matched} matched · ${tele.last_apply.unmatched} unmatched` : "never"}</div>
            </div>
          )}
          <p className="label-mono text-[10px] text-neutral-500">OFFSET = drone clock − camera clock. If every photo is "unmatched", check the drone's timezone and try ±3600.</p>
        </div>
      )}
    </div>
  );
}
